import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

// ============================================================
// Dedup bucket helpers
// ============================================================
function isoWeekBucket(d = new Date()): string {
  // ISO week: YYYY-Www
  const date = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const day = date.getUTCDay() || 7;
  date.setUTCDate(date.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(date.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil((((date.getTime() - yearStart.getTime()) / 86400000) + 1) / 7);
  return `${date.getUTCFullYear()}-W${String(weekNo).padStart(2, "0")}`;
}
function dayBucket(d = new Date()): string {
  return d.toISOString().slice(0, 10);
}

type Severity = "low" | "medium" | "high" | "critical";

interface DetectedAnomaly {
  workspace_id: string;
  account_id: string;
  anomaly_type: string;
  severity: Severity;
  title: string; // data-driven static fallback
  detection_payload: Record<string, any>;
  affected_contact_ids?: string[];
  dedup_key: string;
}

async function insertAnomalies(rows: DetectedAnomaly[]): Promise<number> {
  if (!rows.length) return 0;
  const workspaceIds = [...new Set(rows.map((r) => r.workspace_id))];
  const dedupKeys = [...new Set(rows.map((r) => r.dedup_key).filter(Boolean))];
  const { data: existing } = await supabaseAdmin
    .from("anomalies")
    .select("workspace_id, dedup_key")
    .in("workspace_id", workspaceIds)
    .in("dedup_key", dedupKeys);
  const existingKeys = new Set((existing ?? []).map((r: any) => `${r.workspace_id}::${r.dedup_key}`));
  const freshRows = rows.filter((r) => !existingKeys.has(`${r.workspace_id}::${r.dedup_key}`));
  if (!freshRows.length) return 0;

  const { data, error } = await supabaseAdmin
    .from("anomalies")
    .insert(
      freshRows.map((r) => ({
        workspace_id: r.workspace_id,
        account_id: r.account_id,
        anomaly_type: r.anomaly_type,
        severity: r.severity,
        title: r.title,
        detection_payload: r.detection_payload,
        affected_contact_ids: r.affected_contact_ids ?? [],
        dedup_key: r.dedup_key,
        status: "pending",
        detected_at: new Date().toISOString(),
      })),
    )
    .select("id");
  if (error) {
    console.error("[anomalies] insert error", error);
    return 0;
  }
  return data?.length ?? 0;
}

// ============================================================
// Detectors, one fn per type. Each returns 0..N DetectedAnomaly.
// All use supabaseAdmin (server-only) and operate on a single workspace.
// ============================================================

// 1. Churn spike, week-over-week churn count > 2x baseline (and >= 5 absolute)
async function detectChurnSpike(workspaceId: string, accountId: string): Promise<DetectedAnomaly[]> {
  const { data, error } = await supabaseAdmin.rpc("workspace_dashboard_metrics_v2", { _workspace_id: workspaceId });
  if (error || !data) return [];

  // Counts churned in last 7 days vs prior 7 days (using contacts.date_status_changed when present)
  const since7 = new Date(Date.now() - 7 * 86400000).toISOString();
  const since14 = new Date(Date.now() - 14 * 86400000).toISOString();

  const { data: recent } = await supabaseAdmin
    .from("segments")
    .select("contact_id, contacts!inner(date_status_changed, email)", { count: "exact" })
    .eq("workspace_id", workspaceId)
    .in("segment_name", ["cancelled_paid", "churned_trial"])
    .gte("contacts.date_status_changed", since7);

  const { data: prior } = await supabaseAdmin
    .from("segments")
    .select("contact_id, contacts!inner(date_status_changed)")
    .eq("workspace_id", workspaceId)
    .in("segment_name", ["cancelled_paid", "churned_trial"])
    .gte("contacts.date_status_changed", since14)
    .lt("contacts.date_status_changed", since7);

  const recentCount = recent?.length ?? 0;
  const priorCount = prior?.length ?? 0;
  if (recentCount < 5) return [];
  const ratio = priorCount === 0 ? recentCount : recentCount / priorCount;
  if (ratio < 2) return [];

  const ratioStr = priorCount === 0 ? `${recentCount} new` : `${ratio.toFixed(1)}x`;
  const severity: Severity = recentCount >= 20 ? "critical" : recentCount >= 10 ? "high" : "medium";

  return [{
    workspace_id: workspaceId,
    account_id: accountId,
    anomaly_type: "churn_spike",
    severity,
    title: `Churn jumped ${ratioStr} in the last 7 days, ${recentCount} customer${recentCount === 1 ? "" : "s"} gone`,
    detection_payload: {
      recent_7d_count: recentCount,
      prior_7d_count: priorCount,
      ratio,
      churn_rate: (data as any)?.churn_rate,
    },
    affected_contact_ids: (recent ?? []).map((r: any) => r.contact_id),
    dedup_key: `churn_spike:${isoWeekBucket()}`,
  }];
}

// 2. MRR drop, measured as recurring-customer churn in last 7 days vs current MRR.
//    Uses contacts.date_status_changed (when an event ACTUALLY happened), not import time.
//    Falls back gracefully when segments aren't yet computed: returns [] (no false positive).
async function detectMrrDrop(workspaceId: string, accountId: string): Promise<DetectedAnomaly[]> {
  const { data: metrics } = await supabaseAdmin.rpc("workspace_dashboard_metrics_v2", { _workspace_id: workspaceId });
  const currentMrr = Number((metrics as any)?.mrr ?? 0);
  if (currentMrr <= 0) return [];

  const since7 = new Date(Date.now() - 7 * 86400000).toISOString();

  // Recurring customers who churned in the last 7 days (by actual event date)
  const { data: churned } = await supabaseAdmin
    .from("segments")
    .select("contact_id, contacts!inner(id, email, commission_amount, is_recurring, date_status_changed)")
    .eq("workspace_id", workspaceId)
    .in("segment_name", ["cancelled_paid"])
    .eq("contacts.is_recurring", true)
    .gte("contacts.date_status_changed", since7);

  const rows = (churned ?? []) as any[];
  if (rows.length < 3) return [];
  const lostMrr = rows.reduce((s, r) => s + Number(r.contacts?.commission_amount ?? 0), 0);
  if (lostMrr < currentMrr * 0.1) return [];

  const dropPct = (lostMrr / currentMrr) * 100;
  const severity: Severity = dropPct >= 30 ? "critical" : dropPct >= 20 ? "high" : "medium";
  return [{
    workspace_id: workspaceId,
    account_id: accountId,
    anomaly_type: "mrr_drop",
    severity,
    title: `Lost $${lostMrr.toFixed(0)} MRR this week, ${rows.length} recurring customer${rows.length === 1 ? "" : "s"} churned (${dropPct.toFixed(1)}% of $${currentMrr.toFixed(0)})`,
    detection_payload: { lost_mrr: lostMrr, current_mrr: currentMrr, churned_count: rows.length, drop_pct: dropPct },
    affected_contact_ids: rows.map((r) => r.contact_id),
    dedup_key: `mrr_drop:${isoWeekBucket()}`,
  }];
}

// 3. Conversion drop, trial→paid conversion rate fell >= 25% vs prior period
async function detectConversionDrop(workspaceId: string, accountId: string): Promise<DetectedAnomaly[]> {
  // Use raw_data_json trial timestamps when available
  const since30 = new Date(Date.now() - 30 * 86400000).toISOString();
  const since60 = new Date(Date.now() - 60 * 86400000).toISOString();

  const { data: recentTrials } = await supabaseAdmin
    .from("contacts")
    .select("id, status, date_created")
    .eq("workspace_id", workspaceId)
    .gte("date_created", since30)
    .ilike("status", "%trial%");
  const { data: priorTrials } = await supabaseAdmin
    .from("contacts")
    .select("id, status, date_created")
    .eq("workspace_id", workspaceId)
    .gte("date_created", since60)
    .lt("date_created", since30)
    .ilike("status", "%trial%");

  const recentTotal = recentTrials?.length ?? 0;
  const priorTotal = priorTrials?.length ?? 0;
  if (recentTotal < 10 || priorTotal < 10) return [];

  // Conversions = trials that now have a paid segment
  const ids = (recentTrials ?? []).map((c: any) => c.id);
  const priorIds = (priorTrials ?? []).map((c: any) => c.id);
  const { data: recentPaid } = await supabaseAdmin
    .from("segments").select("contact_id")
    .eq("workspace_id", workspaceId).in("segment_name", ["active_paid", "upgraded_paid"])
    .in("contact_id", ids.length ? ids : ["00000000-0000-0000-0000-000000000000"]);
  const { data: priorPaid } = await supabaseAdmin
    .from("segments").select("contact_id")
    .eq("workspace_id", workspaceId).in("segment_name", ["active_paid", "upgraded_paid"])
    .in("contact_id", priorIds.length ? priorIds : ["00000000-0000-0000-0000-000000000000"]);

  const recentRate = (recentPaid?.length ?? 0) / recentTotal;
  const priorRate = (priorPaid?.length ?? 0) / priorTotal;
  if (priorRate === 0) return [];
  const dropPct = ((priorRate - recentRate) / priorRate) * 100;
  if (dropPct < 25) return [];

  const severity: Severity = dropPct >= 50 ? "high" : "medium";
  return [{
    workspace_id: workspaceId,
    account_id: accountId,
    anomaly_type: "conversion_drop",
    severity,
    title: `Trial→paid conversion fell ${dropPct.toFixed(0)}% (${(priorRate * 100).toFixed(1)}% → ${(recentRate * 100).toFixed(1)}%)`,
    detection_payload: {
      recent_trials: recentTotal, recent_paid: recentPaid?.length ?? 0, recent_rate: recentRate,
      prior_trials: priorTotal, prior_paid: priorPaid?.length ?? 0, prior_rate: priorRate,
      drop_pct: dropPct,
    },
    dedup_key: `conversion_drop:${isoWeekBucket()}`,
  }];
}

// 4. Payment failure cluster, past_due/unpaid >= 5 AND >= 5% of active
async function detectPaymentFailureCluster(workspaceId: string, accountId: string): Promise<DetectedAnomaly[]> {
  const { data } = await supabaseAdmin.rpc("workspace_dashboard_metrics_v2", { _workspace_id: workspaceId });
  const atRisk = Number((data as any)?.at_risk ?? 0);
  const active = Number((data as any)?.total_active ?? 0);
  if (atRisk < 5 || active === 0) return [];
  const pct = (atRisk / active) * 100;
  if (pct < 5) return [];

  const severity: Severity = pct >= 20 ? "critical" : pct >= 10 ? "high" : "medium";
  return [{
    workspace_id: workspaceId,
    account_id: accountId,
    anomaly_type: "payment_failure_cluster",
    severity,
    title: `${atRisk} customers have failed payments, ${pct.toFixed(1)}% of active base at risk`,
    detection_payload: { at_risk: atRisk, active, at_risk_pct: pct },
    dedup_key: `payment_failure:${isoWeekBucket()}`,
  }];
}

// 5. Dormant customer (Definition B): customers who bought a RECURRING product
//    (workspace.config_json.product_tiers[*].payment_structure='recurring') but
//    whose last commission was 35+ days ago, silent attrition the churn metric misses.
//    Excludes: active_trial, cancelled_paid (already counted), and contacts in an
//    expected deferral window (active/renewed payment_schedules with future renewal).
async function detectDormantCustomers(workspaceId: string, accountId: string): Promise<DetectedAnomaly[]> {
  const cutoffIso = new Date(Date.now() - 35 * 86400000).toISOString();

  // 1. Find recurring product+amount pairs from workspace config.
  const { data: ws } = await supabaseAdmin
    .from("workspaces")
    .select("config_json")
    .eq("id", workspaceId)
    .maybeSingle();
  const tiers = ((ws?.config_json as any)?.product_tiers ?? {}) as Record<string, any>;
  const recurringCombos = new Map<string, { product: string; amount: string; payment_structure: string }>();
  for (const [key, val] of Object.entries(tiers)) {
    if ((val as any)?.payment_structure === "recurring") {
      const [product, amt] = key.split("||");
      if (product && amt) {
        const amount = Number(amt).toFixed(2);
        recurringCombos.set(`${product}||${amount}`, { product, amount, payment_structure: "recurring" });
      }
    }
  }
  if (!recurringCombos.size) return [];

  // 2. Candidate customers: last commission for a recurring product+amount is older than cutoff.
  const { data: txRows } = await supabaseAdmin
    .from("transactions")
    .select("contact_id, email, product, commission_amount, transaction_date, created_at, status")
    .eq("workspace_id", workspaceId)
    .limit(10000);

  const buyers = new Map<string, any>();
  for (const tx of (txRows ?? []) as any[]) {
    if (String(tx.status ?? "").toLowerCase() === "cancelled") continue;
    const product = tx.product ?? "";
    const amount = Number(tx.commission_amount).toFixed(2);
    const comboKey = `${product}||${amount}`;
    if (!recurringCombos.has(comboKey)) continue;
    const buyerKey = tx.contact_id ?? tx.email?.toLowerCase();
    if (!buyerKey) continue;
    const last = tx.transaction_date ?? tx.created_at;
    const existing = buyers.get(buyerKey);
    if (!existing || last > existing.last) {
      buyers.set(buyerKey, { id: tx.contact_id, email: tx.email, product, amount, last, comboKey });
    }
  }

  const candidates = Array.from(buyers.values()).filter((c: any) => c.last && c.last < cutoffIso);
  if (!candidates.length) return [];

  const ids = candidates.map((c: any) => c.id).filter(Boolean);

  // 3. Exclude active_trial / cancelled_paid (explicit churn already tracked).
  const { data: segs } = await supabaseAdmin
    .from("segments")
    .select("contact_id, segment_name")
    .eq("workspace_id", workspaceId)
    .in("contact_id", ids.length ? ids : ["00000000-0000-0000-0000-000000000000"])
    .in("segment_name", ["active_trial", "cancelled_paid", "churned_trial"]);
  const excludedBySegment = new Set((segs ?? []).map((s: any) => s.contact_id));

  // 4. Exclude contacts with an active deferral (future renewal).
  const { data: scheds } = await supabaseAdmin
    .from("payment_schedules")
    .select("contact_id, renewal_expected_at, status")
    .eq("workspace_id", workspaceId)
    .in("contact_id", ids.length ? ids : ["00000000-0000-0000-0000-000000000000"])
    .in("status", ["active", "renewed"]);
  const nowIso = new Date().toISOString();
  const deferred = new Set(
    (scheds ?? [])
      .filter((s: any) => s.renewal_expected_at && s.renewal_expected_at > nowIso)
      .map((s: any) => s.contact_id),
  );

  const dormant = candidates.filter((c: any) => !excludedBySegment.has(c.id) && !deferred.has(c.id));
  if (dormant.length < 3) return [];

  const totalValue = dormant.reduce((s, c: any) => s + Number(c.amount), 0);
  const comboCounts = dormant.reduce<Map<string, number>>(
    (m, c: any) => m.set(c.comboKey, (m.get(c.comboKey) ?? 0) + 1),
    new Map<string, number>(),
  );
  const matchedCombos = Array.from(comboCounts.entries()).map(([combo, count]) => ({ combo, count }));
  return [{
    workspace_id: workspaceId,
    account_id: accountId,
    anomaly_type: "dormant_customer",
    severity: dormant.length >= 10 ? "high" : "medium",
    title: `${dormant.length} recurring customer${dormant.length === 1 ? "" : "s"} stopped paying, $${totalValue.toFixed(0)}/cycle silently lost`,
    detection_payload: {
      definition: "B_recurring_silent",
      count: dormant.length,
      total_value_per_cycle: totalValue,
      recurring_product_combos: Array.from(recurringCombos.values()),
      matched_combos: matchedCombos,
      excluded_segments: excludedBySegment.size,
      excluded_deferred: deferred.size,
      samples: dormant.slice(0, 5).map((c: any) => ({
        email: c.email, product: c.product, amount: c.amount, last: c.last,
      })),
    },
    affected_contact_ids: dormant.map((c: any) => c.id).filter(Boolean),
    dedup_key: `dormant:${isoWeekBucket()}`,
  }];
}

// 5b. Missing expected commissions, overdue scheduled payments not received
async function detectMissingExpectedCommissions(workspaceId: string, accountId: string): Promise<DetectedAnomaly[]> {
  const since14 = new Date(Date.now() - 14 * 86400000).toISOString();
  const { data: scheds } = await supabaseAdmin
    .from("payment_schedules")
    .select("contact_id, expected_payments")
    .eq("workspace_id", workspaceId)
    .in("status", ["active", "renewed"]);

  const nowIso = new Date().toISOString();
  let overdueCount = 0;
  let overdueAmt = 0;
  const contactIds = new Set<string>();
  for (const s of (scheds ?? []) as any[]) {
    for (const ep of s.expected_payments ?? []) {
      const status = ep.status ?? "expected";
      const expDate = ep.expected_date;
      if (status === "received" || !expDate) continue;
      if (expDate < nowIso && expDate >= since14) {
        overdueCount += 1;
        overdueAmt += Number(ep.expected_amount ?? 0);
        contactIds.add(s.contact_id);
      }
    }
  }
  if (overdueCount < 3) return [];
  const severity: Severity = overdueCount >= 20 ? "high" : "medium";
  return [{
    workspace_id: workspaceId,
    account_id: accountId,
    anomaly_type: "missing_expected_commissions",
    severity,
    title: `${overdueCount} expected payment${overdueCount === 1 ? "" : "s"} missing, ~$${overdueAmt.toFixed(0)} not received`,
    detection_payload: { overdue_count: overdueCount, overdue_amount: overdueAmt, contact_count: contactIds.size },
    affected_contact_ids: Array.from(contactIds),
    dedup_key: `missing_commissions:${isoWeekBucket()}`,
  }];
}

// 5c. Cross-program churn alert, surfaces reconcile-engine alerts as anomalies (account-wide signal)
async function detectCrossProgramChurn(workspaceId: string, accountId: string): Promise<DetectedAnomaly[]> {
  const since7 = new Date(Date.now() - 7 * 86400000).toISOString();
  const { data: events } = await supabaseAdmin
    .from("activity_events")
    .select("id, contact_id, description, metadata_json, occurred_at")
    .eq("account_id", accountId)
    .eq("workspace_id", workspaceId)
    .eq("event_type", "cross_program_churn_alert")
    .gte("occurred_at", since7);

  const rows = (events ?? []) as any[];
  if (rows.length < 1) return [];
  const totalLtv = rows.reduce((s, e) => s + Number(e.metadata_json?.ltv ?? 0), 0);
  return [{
    workspace_id: workspaceId,
    account_id: accountId,
    anomaly_type: "cross_program_churn_alert",
    severity: rows.length >= 5 ? "high" : "medium",
    title: `${rows.length} customer${rows.length === 1 ? "" : "s"} churned here but still active in other programs, $${totalLtv.toFixed(0)} LTV worth winning back`,
    detection_payload: {
      count: rows.length,
      total_ltv: totalLtv,
      samples: rows.slice(0, 5).map((e) => ({ description: e.description, metadata: e.metadata_json })),
    },
    affected_contact_ids: rows.map((e) => e.contact_id).filter(Boolean),
    dedup_key: `cross_program_churn:${isoWeekBucket()}`,
  }];
}

// 6. New customer surge, last 24h signups > 2x trailing 7-day daily avg (time-sensitive, daily bucket)
async function detectNewCustomerSurge(workspaceId: string, accountId: string): Promise<DetectedAnomaly[]> {
  const since1 = new Date(Date.now() - 1 * 86400000).toISOString();
  const since7 = new Date(Date.now() - 7 * 86400000).toISOString();

  const { count: last24 } = await supabaseAdmin
    .from("contacts").select("id", { count: "exact", head: true })
    .eq("workspace_id", workspaceId).gte("date_created", since1);
  const { count: last7 } = await supabaseAdmin
    .from("contacts").select("id", { count: "exact", head: true })
    .eq("workspace_id", workspaceId).gte("date_created", since7).lt("date_created", since1);

  const day = last24 ?? 0;
  const dailyAvg = (last7 ?? 0) / 6;
  if (day < 5) return [];
  if (dailyAvg > 0 && day / dailyAvg < 2) return [];

  const ratio = dailyAvg > 0 ? day / dailyAvg : day;
  const severity: Severity = ratio >= 5 ? "high" : "medium";
  return [{
    workspace_id: workspaceId,
    account_id: accountId,
    anomaly_type: "new_customer_surge",
    severity,
    title: `${day} new customers in 24h, ${dailyAvg > 0 ? ratio.toFixed(1) + "x" : "first burst of"} the recent daily average`,
    detection_payload: { last_24h: day, prior_6d_total: last7, daily_avg: dailyAvg, ratio },
    dedup_key: `surge:${dayBucket()}`,
  }];
}

// 7. Import anomaly, any failed import in last 24h, OR row count <20% of trailing avg
async function detectImportAnomaly(workspaceId: string, accountId: string): Promise<DetectedAnomaly[]> {
  const since1 = new Date(Date.now() - 1 * 86400000).toISOString();
  const { data: recent } = await supabaseAdmin
    .from("imports")
    .select("id, status, row_count, source_filename, error_message, uploaded_at")
    .eq("workspace_id", workspaceId)
    .gte("uploaded_at", since1)
    .order("uploaded_at", { ascending: false });

  const failed = (recent ?? []).filter((i: any) => i.status === "failed");
  if (failed.length > 0) {
    const first = failed[0] as any;
    return [{
      workspace_id: workspaceId,
      account_id: accountId,
      anomaly_type: "import_anomaly",
      severity: "high",
      title: `${failed.length} import${failed.length === 1 ? "" : "s"} failed in the last 24h${first.source_filename ? ` (latest: ${first.source_filename})` : ""}`,
      detection_payload: {
        failed_count: failed.length,
        latest_error: first.error_message,
        latest_filename: first.source_filename,
        import_ids: failed.map((i: any) => i.id),
      },
      dedup_key: `import_failed:${dayBucket()}`,
    }];
  }

  // Row-count anomaly: latest successful import < 20% of trailing 5-import avg
  const { data: successful } = await supabaseAdmin
    .from("imports")
    .select("row_count, source_filename, uploaded_at")
    .eq("workspace_id", workspaceId)
    .eq("status", "processed")
    .order("uploaded_at", { ascending: false })
    .limit(6);
  if ((successful?.length ?? 0) < 4) return [];
  const arr = successful as any[];
  const latest = arr[0];
  const trailing = arr.slice(1);
  const avg = trailing.reduce((s, i) => s + Number(i.row_count || 0), 0) / trailing.length;
  if (avg < 50) return [];
  if (Number(latest.row_count) >= avg * 0.2) return [];

  return [{
    workspace_id: workspaceId,
    account_id: accountId,
    anomaly_type: "import_anomaly",
    severity: "medium",
    title: `Latest import had ${latest.row_count} rows, typical is ~${avg.toFixed(0)}. Wrong file?`,
    detection_payload: {
      latest_rows: latest.row_count,
      trailing_avg: avg,
      latest_filename: latest.source_filename,
    },
    dedup_key: `import_low_rows:${dayBucket()}`,
  }];
}

// 8. Metric sanity drift, Path Y: VALIDATE outputs, do not re-derive them.
// The v2 RPC is the authoritative MRR calculation. We do not duplicate the per-tier
// formula in TypeScript (that would just be the same math, same bugs, double maintenance).
// Instead we ask different, complementary questions:
//   - Internal consistency: first_tier + second_tier = total MRR? Any negatives?
//   - Sum-of-parts: do the combo breakdowns sum to the totals?
//   - Input integrity: every recurring product_tier has cadence_days? Every product appearing
//     in recent transactions has a matching tier classification?
//   - Bound checks: 2nd-tier share < 50%? MRR week-over-week change < 25% (or import-explained)?
//   - Rule presence: metric_definitions has bad_trial_rules + upgrade_amounts populated?
//   - One-time revenue: sums match between gross and unclassified components?
// Expected on a healthy workspace: zero anomalies.
async function detectMetricSanityDrift(
  workspaceId: string,
  accountId: string,
  opts: { bypassRateLimit?: boolean } = {},
): Promise<DetectedAnomaly[]> {
  // Rate limit: max one sanity check per workspace per hour.
  if (!opts.bypassRateLimit) {
    const oneHourAgo = new Date(Date.now() - 60 * 60 * 1000).toISOString();
    const { data: recent } = await supabaseAdmin
      .from("activity_events")
      .select("id")
      .eq("workspace_id", workspaceId)
      .eq("event_type", "metric_sanity_check")
      .gte("occurred_at", oneHourAgo)
      .limit(1);
    if (recent && recent.length > 0) return [];
  }

  // 1. Authoritative numbers from the v2 RPC.
  const { data: v2, error: v2Err } = await supabaseAdmin.rpc("workspace_dashboard_metrics_v2", {
    _workspace_id: workspaceId,
  });
  if (v2Err || !v2) {
    console.warn("[metric_sanity] v2 rpc unavailable", v2Err?.message);
    return [];
  }
  const dashMrr = Number((v2 as any).mrr ?? 0);
  const dashFirstTier = Number((v2 as any).first_tier_mrr ?? 0);
  const dashSecondTier = Number((v2 as any).second_tier_mrr ?? 0);
  const dashActive = Number((v2 as any).active_monthly ?? 0);
  const dashSecondTierAffiliates = Number((v2 as any).second_tier_affiliates ?? 0);
  const breakdown = (v2 as any).breakdown ?? {};
  const combos: Array<any> = breakdown?.mrr?.combos ?? [];
  const exclusionSource = (v2 as any).exclusion_rules_source ?? "fallback_defaults";

  // 2. Workspace config + metric_definitions.
  const { data: ws } = await supabaseAdmin
    .from("workspaces")
    .select("config_json, metric_definitions")
    .eq("id", workspaceId)
    .single();
  const productTiers: Record<string, any> = (ws?.config_json as any)?.product_tiers ?? {};
  const metricDefs: Record<string, any> = (ws?.metric_definitions as any) ?? {};

  const anomalies: DetectedAnomaly[] = [];
  const issues: Array<{ check: string; detail: string; severity: Severity; payload?: Record<string, any> }> = [];

  // ────────────────────────────────────────────────────────────────
  // Check 1: No metric can be negative.
  // ────────────────────────────────────────────────────────────────
  const negatives: Array<{ name: string; value: number }> = [];
  if (dashMrr < 0) negatives.push({ name: "mrr", value: dashMrr });
  if (dashFirstTier < 0) negatives.push({ name: "first_tier_mrr", value: dashFirstTier });
  if (dashSecondTier < 0) negatives.push({ name: "second_tier_mrr", value: dashSecondTier });
  if (dashActive < 0) negatives.push({ name: "active_monthly", value: dashActive });
  if (negatives.length > 0) {
    issues.push({
      check: "negative_values",
      detail: `${negatives.length} metric(s) are negative, that is never valid.`,
      severity: "high",
      payload: { negatives },
    });
  }

  // ────────────────────────────────────────────────────────────────
  // Check 2: Internal consistency, first_tier + second_tier = total MRR (±$0.05 rounding).
  // ────────────────────────────────────────────────────────────────
  const tierSum = dashFirstTier + dashSecondTier;
  const tierSumDelta = Math.abs(tierSum - dashMrr);
  if (tierSumDelta > 0.05) {
    issues.push({
      check: "tier_sum_consistency",
      detail: `1st-tier MRR ($${dashFirstTier.toFixed(2)}) + 2nd-tier MRR ($${dashSecondTier.toFixed(2)}) = $${tierSum.toFixed(2)}, but total MRR is $${dashMrr.toFixed(2)} (delta $${tierSumDelta.toFixed(2)}).`,
      severity: "high",
      payload: { first_tier: dashFirstTier, second_tier: dashSecondTier, sum: tierSum, total_mrr: dashMrr, delta: tierSumDelta },
    });
  }

  // ────────────────────────────────────────────────────────────────
  // Check 3: Sum-of-parts, sum of combo MRRs equals total (±$0.05).
  // ────────────────────────────────────────────────────────────────
  if (combos.length > 0) {
    const combosTotal = combos.reduce((acc, c) => acc + Number(c?.mrr ?? 0), 0);
    const combosFirst = combos.filter((c) => !c.is_second_tier).reduce((a, c) => a + Number(c?.mrr ?? 0), 0);
    const combosSecond = combos.filter((c) => c.is_second_tier).reduce((a, c) => a + Number(c?.mrr ?? 0), 0);
    const sopDelta = Math.abs(combosTotal - dashMrr);
    if (sopDelta > 0.05) {
      issues.push({
        check: "sum_of_parts",
        detail: `Sum of ${combos.length} MRR combo breakdowns is $${combosTotal.toFixed(2)} but total MRR is $${dashMrr.toFixed(2)}.`,
        severity: "high",
        payload: { combos_total: combosTotal, total_mrr: dashMrr, delta: sopDelta },
      });
    }
    const firstDelta = Math.abs(combosFirst - dashFirstTier);
    const secondDelta = Math.abs(combosSecond - dashSecondTier);
    if (firstDelta > 0.05 || secondDelta > 0.05) {
      issues.push({
        check: "sum_of_parts_per_tier",
        detail: `1st-tier combos sum $${combosFirst.toFixed(2)} vs reported $${dashFirstTier.toFixed(2)} (Δ${firstDelta.toFixed(2)}); 2nd-tier combos sum $${combosSecond.toFixed(2)} vs reported $${dashSecondTier.toFixed(2)} (Δ${secondDelta.toFixed(2)}).`,
        severity: "high",
        payload: { combos_first: combosFirst, reported_first: dashFirstTier, combos_second: combosSecond, reported_second: dashSecondTier },
      });
    }
  }

  // ────────────────────────────────────────────────────────────────
  // Check 4: Input integrity, every recurring/instalment product_tier has cadence_days set.
  // ────────────────────────────────────────────────────────────────
  const missingCadence: string[] = [];
  for (const [key, val] of Object.entries(productTiers)) {
    const v: any = val;
    const structureNeedsCadence = ["recurring", "installment_ends", "installment_renews"].includes(v?.payment_structure);
    if (v?.revenue_type === "recurring" && structureNeedsCadence) {
      const cad = v?.payment_cadence_days;
      if (cad === null || cad === undefined || cad === "" || !Number.isFinite(Number(cad)) || Number(cad) <= 0) {
        missingCadence.push(key);
      }
    }
  }
  if (missingCadence.length > 0) {
    issues.push({
      check: "missing_cadence_days",
      detail: `${missingCadence.length} recurring product tier(s) have no payment_cadence_days set. MRR or renewal projection for these can fall back to a 30-day default, which may be wrong.`,
      severity: "high",
      payload: { missing: missingCadence.slice(0, 20), count: missingCadence.length },
    });
  }

  // ────────────────────────────────────────────────────────────────
  // Check 5: Input integrity, every (product, amount) combo appearing in recent
  // transactions has a matching tier classification in config.
  // ────────────────────────────────────────────────────────────────
  const since30Iso = new Date(Date.now() - 30 * 86400000).toISOString();
  const { data: recentTx } = await supabaseAdmin
    .from("transactions")
    .select("product, commission_amount, status")
    .eq("workspace_id", workspaceId)
    .gte("transaction_date", since30Iso)
    .limit(50000);
  const liveTx = (recentTx ?? []).filter((r: any) => {
    const s = String(r.status ?? "").toLowerCase();
    return !["cancelled", "canceled", "refunded", "failed"].includes(s);
  });
  const seenCombos = new Set<string>();
  for (const r of liveTx as any[]) {
    const product = r.product ?? "";
    const amount = Number(r.commission_amount ?? 0);
    if (!Number.isFinite(amount) || amount <= 0) continue;
    seenCombos.add(`${product}||${amount.toFixed(2)}`);
  }
  const unclassified: string[] = [];
  for (const combo of seenCombos) {
    if (!productTiers[combo]) unclassified.push(combo);
  }
  if (unclassified.length > 0) {
    issues.push({
      check: "unclassified_combos",
      detail: `${unclassified.length} (product, amount) combo(s) seen in the last 30 days have no tier classification. Their revenue is excluded from MRR.`,
      severity: unclassified.length > 5 ? "high" : "medium",
      payload: { unclassified: unclassified.slice(0, 20), count: unclassified.length },
    });
  }

  // ────────────────────────────────────────────────────────────────
  // Check 6: Rule presence, metric_definitions has the exclusion rules populated.
  // ────────────────────────────────────────────────────────────────
  const missingRules: string[] = [];
  if (!metricDefs?.bad_trial_rules?.service_status_excluded?.length) missingRules.push("bad_trial_rules.service_status_excluded");
  if (!metricDefs?.bad_trial_rules?.billing_status_excluded?.length) missingRules.push("bad_trial_rules.billing_status_excluded");
  if (!Array.isArray(metricDefs?.upgrade_amounts)) missingRules.push("upgrade_amounts");
  if (missingRules.length > 0 || exclusionSource !== "metric_definitions") {
    issues.push({
      check: "missing_metric_definitions",
      detail: `metric_definitions is missing keys (${missingRules.join(", ") || "n/a"}); the RPC fell back to hardcoded defaults (source: ${exclusionSource}).`,
      severity: "medium",
      payload: { missing_keys: missingRules, source: exclusionSource },
    });
  }

  // ────────────────────────────────────────────────────────────────
  // Check 7: Bound, 2nd-tier MRR share < 50% of total MRR.
  // ────────────────────────────────────────────────────────────────
  if (dashMrr > 100) {
    const secondShare = (dashSecondTier / dashMrr) * 100;
    if (secondShare > 50) {
      issues.push({
        check: "second_tier_share_bound",
        detail: `2nd-tier MRR is ${secondShare.toFixed(1)}% of total MRR, unusually high. Possible misclassification of 1st-tier products as 2nd-tier.`,
        severity: "medium",
        payload: { second_tier_share_pct: Number(secondShare.toFixed(1)), threshold_pct: 50, second_tier_mrr: dashSecondTier, total_mrr: dashMrr },
      });
    }
  }

  // ────────────────────────────────────────────────────────────────
  // Check 8: Bound, MRR change vs prior snapshot < 25% (unless an import happened since).
  // We store our own snapshots in activity_events (event_type=metric_snapshot), no schema change.
  // ────────────────────────────────────────────────────────────────
  const { data: prevSnapshots } = await supabaseAdmin
    .from("activity_events")
    .select("occurred_at, metadata_json")
    .eq("workspace_id", workspaceId)
    .eq("event_type", "metric_snapshot")
    .order("occurred_at", { ascending: false })
    .limit(1);
  const prev = prevSnapshots?.[0];
  if (prev && Number((prev.metadata_json as any)?.mrr ?? NaN) > 0) {
    const prevMrr = Number((prev.metadata_json as any).mrr);
    const change = Math.abs(dashMrr - prevMrr) / prevMrr;
    if (change > 0.25 && Math.abs(dashMrr - prevMrr) > 50) {
      // Check whether an import (which would naturally explain the swing) happened since.
      const { data: importsSince } = await supabaseAdmin
        .from("imports")
        .select("id")
        .eq("workspace_id", workspaceId)
        .gte("uploaded_at", prev.occurred_at)
        .limit(1);
      if (!importsSince || importsSince.length === 0) {
        issues.push({
          check: "mrr_unexpected_swing",
          detail: `MRR changed ${(change * 100).toFixed(1)}% (from $${prevMrr.toFixed(2)} to $${dashMrr.toFixed(2)}) since ${new Date(prev.occurred_at as string).toISOString().slice(0, 10)} without an import to explain it.`,
          severity: "medium",
          payload: { prev_mrr: prevMrr, current_mrr: dashMrr, change_pct: Number((change * 100).toFixed(1)), prev_snapshot_at: prev.occurred_at },
        });
      }
    }
  }

  // ────────────────────────────────────────────────────────────────
  // Check 9: One-time revenue internal consistency.
  // Sum non-recurring tx (last 30d) vs sum of one-time classified combos; flag negatives or mismatch in components.
  // ────────────────────────────────────────────────────────────────
  let oneTimeGross = 0;
  let oneTimeRecurringMix = 0; // tx with amount matching a recurring combo should NOT count
  for (const r of liveTx as any[]) {
    const amount = Number(r.commission_amount ?? 0);
    if (!Number.isFinite(amount)) continue;
    const key = `${r.product ?? ""}||${amount.toFixed(2)}`;
    const tier: any = productTiers[key];
    if (!tier) continue; // already flagged in check 5
    if (tier.revenue_type === "one_time") {
      if (amount < 0) {
        // negative one-time tx is a refund-shaped row that wasn't filtered, flag.
        issues.push({
          check: "negative_one_time_tx",
          detail: `One-time transaction with negative amount $${amount.toFixed(2)} for ${key}, should be cancelled/refunded but status is ${r.status}.`,
          severity: "low",
          payload: { combo: key, amount, status: r.status },
        });
      }
      oneTimeGross += amount;
    } else if (tier.revenue_type === "recurring") {
      oneTimeRecurringMix += amount;
    }
  }

  // ────────────────────────────────────────────────────────────────
  // Snapshot for next run + activity event (also serves as our rate-limit marker).
  // ────────────────────────────────────────────────────────────────
  await supabaseAdmin.from("activity_events").insert({
    workspace_id: workspaceId,
    account_id: accountId,
    event_type: "metric_sanity_check",
    description: `Sanity check ran: ${issues.length} issue(s) detected.`,
    metadata_json: {
      issues_count: issues.length,
      checks_run: 9,
      issue_summary: issues.map((i) => i.check),
    },
  });
  await supabaseAdmin.from("activity_events").insert({
    workspace_id: workspaceId,
    account_id: accountId,
    event_type: "metric_snapshot",
    description: `MRR=$${dashMrr.toFixed(2)}, 1st=$${dashFirstTier.toFixed(2)}, 2nd=$${dashSecondTier.toFixed(2)}, active=${dashActive}`,
    metadata_json: {
      mrr: dashMrr,
      first_tier_mrr: dashFirstTier,
      second_tier_mrr: dashSecondTier,
      active_monthly: dashActive,
      second_tier_affiliates: dashSecondTierAffiliates,
      one_time_gross_30d: Number(oneTimeGross.toFixed(2)),
    },
  });

  // ────────────────────────────────────────────────────────────────
  // Emit anomalies, one per check that failed.
  // ────────────────────────────────────────────────────────────────
  for (const i of issues) {
    anomalies.push({
      workspace_id: workspaceId,
      account_id: accountId,
      anomaly_type: "metric_sanity_drift",
      severity: i.severity,
      title: i.detail.length > 200 ? i.detail.slice(0, 197) + "…" : i.detail,
      detection_payload: {
        check: i.check,
        ...((i.payload ?? {}) as Record<string, any>),
        philosophy: "Path Y validation: dashboard MRR is treated as authoritative; this check validates inputs and consistency, not re-derived outputs.",
      },
      dedup_key: `metric_sanity:${i.check}:${dayBucket()}`,
    });
  }

  return anomalies;
}

// ============================================================
// Orchestrators
// ============================================================
const TIME_SENSITIVE = new Set(["new_customer_surge", "import_anomaly", "metric_sanity_drift"]);


async function runDetectorsForWorkspace(
  workspaceId: string,
  accountId: string,
  opts: { timeSensitiveOnly?: boolean; bypassMetricSanityRateLimit?: boolean } = {},
): Promise<{ inserted: number; by_type: Record<string, number>; samples: Record<string, any> }> {
  const all: Array<{ name: string; fn: () => Promise<DetectedAnomaly[]> }> = [
    { name: "churn_spike", fn: () => detectChurnSpike(workspaceId, accountId) },
    { name: "mrr_drop", fn: () => detectMrrDrop(workspaceId, accountId) },
    { name: "conversion_drop", fn: () => detectConversionDrop(workspaceId, accountId) },
    { name: "payment_failure_cluster", fn: () => detectPaymentFailureCluster(workspaceId, accountId) },
    { name: "dormant_customer", fn: () => detectDormantCustomers(workspaceId, accountId) },
    { name: "missing_expected_commissions", fn: () => detectMissingExpectedCommissions(workspaceId, accountId) },
    { name: "cross_program_churn_alert", fn: () => detectCrossProgramChurn(workspaceId, accountId) },
    { name: "new_customer_surge", fn: () => detectNewCustomerSurge(workspaceId, accountId) },
    { name: "import_anomaly", fn: () => detectImportAnomaly(workspaceId, accountId) },
    { name: "metric_sanity_drift", fn: () => detectMetricSanityDrift(workspaceId, accountId, { bypassRateLimit: opts.bypassMetricSanityRateLimit }) },
  ];

  const detectors = opts.timeSensitiveOnly
    ? all.filter((d) => TIME_SENSITIVE.has(d.name))
    : all;

  const by_type: Record<string, number> = {};
  const samples: Record<string, any> = {};
  const toInsert: DetectedAnomaly[] = [];

  for (const d of detectors) {
    try {
      const found = await d.fn();
      by_type[d.name] = found.length;
      if (found.length) samples[d.name] = found[0].detection_payload;
      toInsert.push(...found);
    } catch (err) {
      console.error(`[anomaly:${d.name}]`, err);
      by_type[d.name] = -1;
    }
  }

  const inserted = await insertAnomalies(toInsert);
  return { inserted, by_type, samples };
}

// ============================================================
// Server functions
// ============================================================

// Manual / on-demand: run all detectors for a single workspace
export const runAnomalyDetectorsForWorkspace = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(z.object({ workspace_id: z.string().uuid() }).parse)
  .handler(async ({ data, context }) => {
    const { data: ws } = await supabaseAdmin
      .from("workspaces").select("account_id").eq("id", data.workspace_id).maybeSingle();
    if (!ws) throw new Error("Workspace not found");
    // RLS check via auth ctx
    const { data: member } = await supabaseAdmin
      .from("account_members").select("user_id")
      .eq("account_id", ws.account_id).eq("user_id", context.userId).maybeSingle();
    if (!member) throw new Error("Forbidden");
    return runDetectorsForWorkspace(data.workspace_id, ws.account_id, { bypassMetricSanityRateLimit: true });
  });

// Run all detectors for every workspace in the user's account
export const runAnomalyDetectorsForAccount = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data: members } = await supabaseAdmin
      .from("account_members").select("account_id").eq("user_id", context.userId);
    const accountIds = (members ?? []).map((m: any) => m.account_id);
    if (!accountIds.length) return { inserted: 0, workspaces: 0, by_type: {} };

    const { data: workspaces } = await supabaseAdmin
      .from("workspaces").select("id, account_id").in("account_id", accountIds);

    const agg: Record<string, number> = {};
    const samples: Record<string, any> = {};
    let inserted = 0;
    for (const w of workspaces ?? []) {
      const res = await runDetectorsForWorkspace((w as any).id, (w as any).account_id, { bypassMetricSanityRateLimit: true });
      inserted += res.inserted;
      for (const [k, v] of Object.entries(res.by_type)) {
        agg[k] = (agg[k] ?? 0) + Math.max(v, 0);
        if (!samples[k] && res.samples[k]) samples[k] = res.samples[k];
      }
    }
    return { inserted, workspaces: workspaces?.length ?? 0, by_type: agg, samples };
  });

// Internal helper for the import pipeline: run only time-sensitive detectors
export async function runTimeSensitiveDetectorsAfterImport(workspaceId: string): Promise<void> {
  const { data: ws } = await supabaseAdmin
    .from("workspaces").select("account_id").eq("id", workspaceId).maybeSingle();
  if (!ws) return;
  await runDetectorsForWorkspace(workspaceId, (ws as any).account_id, { timeSensitiveOnly: true });
}
